/**
 * Path-style subject URLs (07-03). Legacy query routes remain as redirects.
 * Example: /orders/BQ-4K2M/investigate
 */

export type SubjectLocation = {
  caseId: string | null;
  orderId: string | null;
  customerId: string | null;
  businessId: string | null;
  paymentRef: string | null;
  auditRef: string | null;
  leaf: 'case' | 'order' | 'payment' | 'delivery' | 'customer' | 'business' | 'audit' | 'other';
};

function queryString(params: Record<string, string | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) sp.set(key, value);
  }
  const q = sp.toString();
  return q ? `?${q}` : '';
}

export function caseDetailPath(caseId: string, extra?: { orderId?: string }): string {
  return `/cases/${encodeURIComponent(caseId)}${queryString({
    caseId,
    orderId: extra?.orderId,
  })}`;
}

export function orderInvestigatePath(orderId: string, extra?: { caseId?: string }): string {
  return `/orders/${encodeURIComponent(orderId)}/investigate${queryString({
    orderId,
    caseId: extra?.caseId,
  })}`;
}

export function paymentTracePath(orderId: string, extra?: { caseId?: string }): string {
  return `/orders/${encodeURIComponent(orderId)}/payment${queryString({
    orderId,
    caseId: extra?.caseId,
  })}`;
}

export function deliveryTimelinePath(orderId: string, extra?: { caseId?: string }): string {
  return `/orders/${encodeURIComponent(orderId)}/delivery${queryString({
    orderId,
    caseId: extra?.caseId,
  })}`;
}

export function customer360Path(customerId: string, extra?: { caseId?: string }): string {
  return `/customers/${encodeURIComponent(customerId)}${queryString({
    customerId,
    caseId: extra?.caseId,
  })}`;
}

export function merchant360Path(businessId: string, extra?: { caseId?: string }): string {
  return `/businesses/${encodeURIComponent(businessId)}${queryString({
    businessId,
    caseId: extra?.caseId,
  })}`;
}

export function auditSearchPath(auditRef: string): string {
  return `/audit/${encodeURIComponent(auditRef)}${queryString({ q: auditRef })}`;
}

export function displaySubjectRef(id: string): string {
  if (/^[A-Z]{2,}-/i.test(id)) return id;
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

const PATH_PATTERNS: Array<{
  re: RegExp;
  leaf: SubjectLocation['leaf'];
  key: keyof Omit<SubjectLocation, 'leaf'>;
}> = [
  { re: /^\/cases\/([^/]+)\/macros\/?$/, leaf: 'case', key: 'caseId' },
  { re: /^\/cases\/([^/]+)\/?$/, leaf: 'case', key: 'caseId' },
  { re: /^\/orders\/([^/]+)\/investigate\/?$/, leaf: 'order', key: 'orderId' },
  { re: /^\/orders\/([^/]+)\/payment\/?$/, leaf: 'payment', key: 'orderId' },
  { re: /^\/orders\/([^/]+)\/delivery\/?$/, leaf: 'delivery', key: 'orderId' },
  { re: /^\/customers\/([^/]+)\/?$/, leaf: 'customer', key: 'customerId' },
  { re: /^\/businesses\/([^/]+)\/?$/, leaf: 'business', key: 'businessId' },
  { re: /^\/audit\/([^/]+)\/?$/, leaf: 'audit', key: 'auditRef' },
];

const LEGACY_LEAF: Record<string, SubjectLocation['leaf']> = {
  '/case-detail': 'case',
  '/order-investigation': 'order',
  '/payment-trace': 'payment',
  '/delivery-timeline': 'delivery',
  '/customer-360': 'customer',
  '/merchant-360': 'business',
  '/audit-search': 'audit',
};

export function parseSubjectFromLocation(
  pathname: string,
  searchParams: URLSearchParams,
): SubjectLocation {
  const fromQuery: SubjectLocation = {
    caseId: searchParams.get('caseId'),
    orderId: searchParams.get('orderId'),
    customerId: searchParams.get('customerId'),
    businessId: searchParams.get('businessId'),
    paymentRef: searchParams.get('paymentId') ?? searchParams.get('paymentRef'),
    auditRef: searchParams.get('auditRef') ?? searchParams.get('q'),
    leaf: 'other',
  };

  for (const pattern of PATH_PATTERNS) {
    const match = pathname.match(pattern.re);
    if (!match?.[1]) continue;
    const decoded = decodeURIComponent(match[1]);
    return {
      ...fromQuery,
      [pattern.key]: decoded,
      leaf: pattern.leaf,
      caseId: pattern.key === 'caseId' ? decoded : fromQuery.caseId,
      orderId: pattern.key === 'orderId' ? decoded : fromQuery.orderId,
    };
  }

  const legacyLeaf = LEGACY_LEAF[pathname];
  if (legacyLeaf) {
    return { ...fromQuery, leaf: legacyLeaf };
  }
  return fromQuery;
}

export function isInvestigationPath(pathname: string): boolean {
  return (
    PATH_PATTERNS.some((p) => p.re.test(pathname)) ||
    pathname in LEGACY_LEAF ||
    pathname === '/issue-refund' ||
    pathname === '/grant-goodwill' ||
    pathname === '/waive-fee' ||
    pathname === '/create-replacement' ||
    pathname === '/cancel-order-support' ||
    pathname === '/attribute-liability' ||
    pathname === '/remedy-history' ||
    pathname === '/escalate' ||
    pathname === '/four-eyes-confirmation' ||
    pathname === '/refund-offer-composer' ||
    pathname === '/delivery-failure-case' ||
    pathname === '/case-macros' ||
    /\/cases\/[^/]+\/macros\/?$/.test(pathname)
  );
}
